import type {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridValidRowModel,
} from './types';

const empty = (value: unknown) => value === undefined || value === null || value === '';
const text = (value: unknown) => String(value ?? '').toLocaleLowerCase();

export function changeFilterOperator(item: GridFilterItem, next: string): GridFilterItem {
  const value =
    next === 'isAnyOf'
      ? Array.isArray(item.value)
        ? item.value
        : empty(item.value)
          ? []
          : [item.value]
      : Array.isArray(item.value)
        ? (item.value[0] ?? '')
        : item.value;
  return { ...item, operator: next, value };
}

export function durationDisplayValue(item: GridFilterItem): string {
  if (empty(item.value)) return '';
  return String(Number((Number(item.value) / (item.unitMultiplier || 1)).toFixed(10)));
}

export function updateDurationValue(
  item: GridFilterItem,
  value: string,
  unit = item.unitMultiplier || 1,
): GridFilterItem {
  return { ...item, unitMultiplier: unit, value: value.trim() === '' ? '' : Number(value) * unit };
}
const operator = (
  value: string,
  label: string,
  compare: (a: any, b: any) => boolean,
  requiresFilterValue = true,
): GridFilterOperator => ({
  value,
  label,
  requiresFilterValue,
  getApplyFilterFn: (item) =>
    requiresFilterValue && (empty(item.value) || (Array.isArray(item.value) && !item.value.length))
      ? null
      : (cell) => compare(cell, item.value),
});
const emptyOperators = [
  operator('isEmpty', 'is empty', (a) => empty(a), false),
  operator('isNotEmpty', 'is not empty', (a) => !empty(a), false),
];
export const stringOperators = (): GridFilterOperator[] => [
  operator('contains', 'contains', (a, b) => text(a).includes(text(b))),
  operator('doesNotContain', 'does not contain', (a, b) => !text(a).includes(text(b))),
  operator('equals', 'equals', (a, b) => text(a) === text(b)),
  operator('doesNotEqual', 'does not equal', (a, b) => text(a) !== text(b)),
  operator('startsWith', 'starts with', (a, b) => text(a).startsWith(text(b))),
  operator('endsWith', 'ends with', (a, b) => text(a).endsWith(text(b))),
  ...emptyOperators,
  operator(
    'isAnyOf',
    'is any of',
    (a, b) => Array.isArray(b) && b.some((v) => text(v) === text(a)),
  ),
];
export const numericOperators = (): GridFilterOperator[] => [
  ...(['=', '!=', '>', '>=', '<', '<='] as const).map((op) =>
    operator(
      op,
      {
        '=': 'equals',
        '!=': 'does not equal',
        '>': 'more than',
        '>=': 'at least',
        '<': 'less than',
        '<=': 'at most',
      }[op],
      (a, b) => {
        if (empty(a) || !Number.isFinite(Number(a)) || !Number.isFinite(Number(b))) return false;
        const x = Number(a);
        const y = Number(b);
        switch (op) {
          case '=':
            return x === y;
          case '!=':
            return x !== y;
          case '>':
            return x > y;
          case '>=':
            return x >= y;
          case '<':
            return x < y;
          case '<=':
            return x <= y;
        }
      },
    ),
  ),
  ...emptyOperators,
  operator(
    'isAnyOf',
    'is any of',
    (a, b) => !empty(a) && Array.isArray(b) && b.some((v) => Number(v) === Number(a)),
  ),
];
export const singleSelectOperators = (): GridFilterOperator[] => [
  operator('is', 'is', (a, b) => String(a) === String(b)),
  operator('not', 'is not', (a, b) => String(a) !== String(b)),
  operator(
    'isAnyOf',
    'is any of',
    (a, b) => Array.isArray(b) && b.some((v) => String(v) === String(a)),
  ),
];
const DAY_VALUE = /^\d{4}-\d{2}-\d{2}$/;
const MINUTE_VALUE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
/** The local calendar day, or the minute once the filter carries a time. */
function point(value: any, unit: 'day' | 'minute'): number {
  const date = new Date(value);
  return unit === 'day'
    ? new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    : Math.floor(date.getTime() / 60_000);
}
/** A date filter's value, split into what its date and time inputs show. */
export function dateFilterParts(value: unknown): { date: string; time: string } {
  const text = empty(value) ? '' : String(value);
  return { date: text.slice(0, 10), time: text.slice(11, 16) };
}
/** A date filter's value from its inputs: the day alone ("2026-09-21") until a time is set. */
export function dateFilterValue(date: string, time: string): string {
  return date ? (time ? `${date}T${time}` : date) : '';
}
export const dateOperators = (): GridFilterOperator[] => [
  ...(['is', 'not', 'after', 'onOrAfter', 'before', 'onOrBefore'] as const).map((op) =>
    operator(
      op,
      {
        is: 'is',
        not: 'is not',
        after: 'is after',
        onOrAfter: 'is on or after',
        before: 'is before',
        onOrBefore: 'is on or before',
      }[op],
      (a, b) => {
        if (empty(a)) return false;
        // Input values denote local days and minutes, never UTC. A day compares whole days, so
        // "is" matches any time that day; a day and time compares to the minute.
        const unit = typeof b === 'string' && MINUTE_VALUE.test(b) ? 'minute' : 'day';
        const x = point(a, unit);
        const y = point(typeof b === 'string' && DAY_VALUE.test(b) ? `${b}T00:00:00` : b, unit);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
        switch (op) {
          case 'is':
            return x === y;
          case 'not':
            return x !== y;
          case 'after':
            return x > y;
          case 'onOrAfter':
            return x >= y;
          case 'before':
            return x < y;
          case 'onOrBefore':
            return x <= y;
        }
      },
    ),
  ),
  ...emptyOperators,
];
export function getOperators(column: GridColDef): GridFilterOperator[] {
  if (column.filterOperators) return column.filterOperators;
  switch (column.type) {
    case 'number':
      return numericOperators();
    case 'date':
    case 'dateTime':
      return dateOperators();
    case 'singleSelect':
      return singleSelectOperators();
    case 'boolean':
      return singleSelectOperators().slice(0, 1);
    default:
      return stringOperators();
  }
}
export function getCellValue<R extends GridValidRowModel>(row: R, column: GridColDef<R>): any {
  return column.valueGetter ? column.valueGetter(row[column.field], row) : row[column.field];
}
/** Whether a filter filters anything yet. A condition still waiting for its value doesn't. */
export function isFilterItemActive(columns: readonly GridColDef[], item: GridFilterItem): boolean {
  const column = columns.find((c) => c.field === item.field);
  return !!(
    column &&
    getOperators(column)
      .find((op) => op.value === item.operator)
      ?.getApplyFilterFn(item)
  );
}
export function createRowFilter<R extends GridValidRowModel>(
  columns: readonly GridColDef<R>[],
  model: GridFilterModel,
): (row: R) => boolean {
  const conditions = model.items.flatMap((item: GridFilterItem) => {
    const column = columns.find((c) => c.field === item.field);
    if (!column) return [];
    const predicate = getOperators(column)
      .find((op) => op.value === item.operator)
      ?.getApplyFilterFn(item);
    return predicate ? [(row: R) => predicate(getCellValue(row, column))] : [];
  });
  return (row) =>
    !conditions.length ||
    (model.logicOperator === 'or'
      ? conditions.some((test) => test(row))
      : conditions.every((test) => test(row)));
}

/**
 * Drops each section row whose group (the rows up to the next section) is empty after filtering,
 * so a search never leaves a "Pinned" header above nothing.
 */
export function withoutEmptySections<T>(rows: T[], isSection: (row: T) => boolean): T[] {
  return rows.filter(
    (row, index) => !isSection(row) || (index + 1 < rows.length && !isSection(rows[index + 1])),
  );
}
